import React, {
  useState,
  useEffect,
  useRef,
  useContext,
  forwardRef,
  useImperativeHandle,
} from "react";
import apiConfig from "../../config/apiConfig";

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchForeignResource } from "../../apis/resources";
import { fetchEnum } from "../../apis/enum";
import { useParams } from "react-router-dom";
import { apiFetch } from "../../apis/apiFetch";

import { useRaspStore } from "../../store/raspStore";
import { useGraduationdetailsViewModel } from "../../viewModels/useGraduationdetailsViewModel";
import { LoginContext } from "../../context/LoginContext";
import "./FormStyles.css";
import { universityList } from "../../utils/university_list";
import { validate } from "../../utils/validator";
import { graduationValidationRules } from "../../validators/applicantValidator";

export type resourceMetaData = {
  resource: string;
  fieldValues: any[];
};

// getCookie removed - using apiFetch

export interface GraduationdetailsFormRef {
  save: () => Promise<void>;
  getErrors: () => Record<string, string>;
}

const DEGREE_OPTIONS = [
  "Bachelor of Engineering (B.E.)",
  "Bachelor of Technology (B.Tech)",
  "Equivalent (B.E./B.Tech)",
];

const CreateGraduationdetails = forwardRef<GraduationdetailsFormRef>(
  (props, ref) => {
    const { user } = useContext(LoginContext); // logged-in user

    const [resMetaData, setResMetaData] = useState<resourceMetaData[]>([]);
    const [showToast, setShowToast] = useState<any>(false);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [searchQueries, setSearchQueries] = useState<Record<string, string>>(
      {},
    );
    const regex = /^(g_|archived|extra_data)/;
    const apiUrl = apiConfig.getResourceUrl("applications");
    const metadataUrl = apiConfig.getResourceMetaDataUrl("applications");
    const [isOtherUniversity, setIsOtherUniversity] = useState(false);

    const fetchedResources = useRef(new Set<string>());
    const fetchedEnum = useRef(new Set<string>());
    const hasPrePopulated = useRef(false);
    const queryClient = useQueryClient();

    // userId is now managed via LoginContext

    const { appId }: any = useParams<any>();

    const {
      fields,
      setFields,
      enums,
      setEnums,
      foreignKeyData,
      setForeignKeyData,
      dataToSave,
      setDataToSave,
      loadMetadata,
      save,
    } = useGraduationdetailsViewModel(user?.userId || "", appId || "");

    useEffect(() => {
      loadMetadata();
    }, []);

    // Auto-set user_id from logged-in user's email
    useEffect(() => {
      if (!user || !user.email_id) return;
      const users = foreignKeyData["Users"] || [];
      if (!users.length) return;

      const match = users.find((u: any) => {
        const userEmail = (u.email || u.email_id || u.user_email || "")
          .toLowerCase()
          .trim();
        return userEmail === user.email_id.toLowerCase().trim();
      });

      if (match && match.id) {
        setDataToSave((prev: any) => ({
          ...prev,
          user_id: match.id,
          level: "UG",
        }));
      } else {
        setDataToSave((prev: any) => ({
          ...prev,
          level: "UG",
        }));
      }
    }, [user, foreignKeyData]);

    const fetchForeignData = async (
      foreignResource: string,
      fieldName: string,
      foreignField: string,
    ) => {
      try {
        const data = await fetchForeignResource(foreignResource);
        setForeignKeyData((prev: any) => ({
          ...prev,
          [foreignResource]: data,
        }));
      } catch (err) {
        console.error(`Error fetching foreign data for ${fieldName}:`, err);
      }
    };

    const fetchEnumData = async (enumName: string) => {
      try {
        const data = await fetchEnum(enumName);
        setEnums((prev: any) => ({
          ...prev,
          [enumName]: data,
        }));
      } catch (err) {
        console.error(`Error fetching enum data for ${enumName}:`, err);
      }
    };

    const {
      data: metaData,
      isLoading,
      error,
    } = useQuery({
      queryKey: ["resMetaData", "GraduationdetailsCreate"],
      queryFn: async () => {
        const res = await fetch(metadataUrl, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        });

        if (!res.ok) {
          throw new Error(`Failed to fetch metadata: ${res.statusText}`);
        }

        const data = await res.json();

        setResMetaData(data);
        setFields(data[0].fieldValues);

        const foreignFields = data[0].fieldValues.filter(
          (field: any) => field.foreign,
        );
        for (const field of foreignFields) {
          if (!fetchedResources.current.has(field.foreign)) {
            fetchedResources.current.add(field.foreign);

            queryClient.prefetchQuery({
              queryKey: ["foreignData", field.foreign],
              queryFn: () => fetchForeignResource(field.foreign),
            });

            await fetchForeignData(
              field.foreign,
              field.name,
              field.foreign_field,
            );
          }
        }

        const enumFields = data[0].fieldValues.filter(
          (field: any) => field.isEnum === true,
        );
        for (const field of enumFields) {
          if (!fetchedEnum.current.has(field.possible_value)) {
            fetchedEnum.current.add(field.possible_value);

            queryClient.prefetchQuery({
              queryKey: ["enum", field.possible_value],
              queryFn: () => fetchEnum(field.possible_value),
            });

            await fetchEnumData(field.possible_value);
          }
        }

        return data;
      },
    });

    useEffect(() => {
      console.log("data to save", dataToSave);
    }, [dataToSave]);

    const {
      data: dataRes,
      isLoading: isLoadingDataRes,
      error: errorDataRes,
    } = useQuery({
      queryKey: ["resourceData", "GraduationdetailsCreate", user?.userId, localStorage.getItem("activeDriveId")],
      queryFn: async () => {
        if (!user?.userId) return [];
        const response = await apiFetch(
          `${apiConfig.getResourceUrl("applications")}?queryId=GET_APPLICATIONS_BY_USER_ID&args=user_id:${user.userId}`,
          {
            method: "GET",
          },
        );

        if (!response.ok) {
          throw new Error("Error: " + response.status);
        }

        const data = await response.json();
        return data.resource || [];
      },
      enabled: !!user?.userId,
    });

    useEffect(() => {
      const activeDriveId = localStorage.getItem("activeDriveId");
      if (dataRes && activeDriveId) {
        const activeRecord = dataRes.find((r: any) => r.drive_id === activeDriveId);
        let dataToStore = { resource: "Graduationdetails", records: activeRecord ? [activeRecord] : [] };
        useRaspStore
          .getState()
          .initializeStore(user?.userId || "", dataToStore);
      }
    }, [dataRes, user]);

    // Pre-populate form with existing data when user_id is resolved and records are loaded
    useEffect(() => {
      const activeDriveId = localStorage.getItem("activeDriveId");
      if (
        !hasPrePopulated.current &&
        dataRes &&
        dataRes.length > 0 &&
        dataToSave.user_id &&
        activeDriveId
      ) {
        const userRecord = dataRes.find(
          (r: any) => r.user_id === dataToSave.user_id && r.drive_id === activeDriveId,
        );
        if (userRecord) {
          hasPrePopulated.current = true;
          setDataToSave((prev: any) => ({ ...userRecord, ...prev }));
        }
      }
    }, [dataRes, dataToSave.user_id]);

    // Compute percentage from marks
    const marksObtained = Number(dataToSave["marks_obtained"]) || 0;
    const maximumMarks = Number(dataToSave["maximum_marks"]) || 0;
    let rawPercentage =
      maximumMarks > 0 ? (marksObtained / maximumMarks) * 100 : 0;
    const percentage =
      rawPercentage > 100 || marksObtained < 0 || maximumMarks < 0
        ? 0
        : rawPercentage;
    const isPercentageValid =
      maximumMarks === 0 || (percentage >= 65 && percentage <= 100);

    // Auto-populate cgpa with calculated percentage
    useEffect(() => {
      if (maximumMarks > 0) {
        setDataToSave((prev: any) => ({
          ...prev,
          cgpa: parseFloat(percentage.toFixed(2)),
        }));
      }
    }, [marksObtained, maximumMarks]);

    const handleCreate = async () => {
      const vErrors = validate(dataToSave, graduationValidationRules);
      setErrors(vErrors);

      await save();
      queryClient.invalidateQueries({
        queryKey: ["resourceData", "GraduationdetailsCreate"],
      });
    };

    useImperativeHandle(ref, () => ({
      save: handleCreate,
      getErrors: () => errors,
    }));

    const handleSearchChange = (fieldName: string, value: string) => {
      setSearchQueries((prev) => ({ ...prev, [fieldName]: value }));
    };

    const collegename = dataToSave["collegename"];

    useEffect(() => {
      if (collegename && !universityList.includes(collegename)) {
        setIsOtherUniversity(true);
      }
    }, [collegename]);

    return (
      <div className="form-container">
        <h3 className="form-subtitle">Graduation Details</h3>

        <div
          style={{
            marginBottom: "20px",
            padding: "12px",
            backgroundColor: "#e0f2fe",
            borderRadius: "6px",
            border: "1px solid #bae6fd",
            fontSize: "14px",
          }}
        >
          <strong style={{ color: "#0369a1" }}>
            Check Eligibility Criteria:{" "}
          </strong>
          <a
            href="https://www.iiitb.ac.in/courses/mtech-cse-admissions#title-tab13023"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: "#0284c7",
              textDecoration: "underline",
              marginLeft: "8px",
              fontWeight: "500",
            }}
          >
            M.Tech CSE
          </a>
          <span style={{ margin: "0 10px", color: "#7dd3fc" }}>|</span>
          <a
            href="https://www.iiitb.ac.in/courses/mtech-ece-admissions#title-tab13001"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: "#0284c7",
              textDecoration: "underline",
              fontWeight: "500",
            }}
          >
            M.Tech ECE
          </a>
          <span style={{ margin: "0 10px", color: "#7dd3fc" }}>|</span>
          <a
            href="https://www.iiitb.ac.in/courses/mtech-ai-ds-admissions#title-tab13013"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: "#0284c7",
              textDecoration: "underline",
              fontWeight: "500",
            }}
          >
            M.Tech AI&DS
          </a>
          <div style={{ marginTop: "6px" }}>
            <strong style={{ color: "#0369a1" }}>
              Note: Please click on the "Eligibility" tab on the page that
              opens.
            </strong>
          </div>
        </div>

        <div className="form-grid">
          {/* Row 1 */}

          <div className="form-group">
            <label className="form-label">University Name *</label>

            <div className="select-wrapper">
              <select
                className={`form-input ${errors.collegename ? "input-error" : ""}`}
                value={
                  universityList.some(
                    (u) => u.toLowerCase() === collegename?.toLowerCase(),
                  )
                    ? collegename
                    : collegename
                      ? "OTHER"
                      : ""
                }
                onChange={(e) => {
                  if (e.target.value === "OTHER") {
                    setIsOtherUniversity(true);
                    setDataToSave({ ...dataToSave, collegename: "" });
                  } else {
                    setIsOtherUniversity(false);
                    setDataToSave({
                      ...dataToSave,
                      collegename: e.target.value,
                    });
                  }
                  if (errors.collegename)
                    setErrors((prev) => ({ ...prev, collegename: "" }));
                }}
              >
                <option value="">Select University</option>

                {[...universityList].sort().map((uni, index) => (
                  <option key={index} value={uni}>
                    {uni}
                  </option>
                ))}

                <option value="OTHER">OTHERS</option>
              </select>
              {errors.collegename && (
                <span
                  className="error-text"
                  style={{ position: "absolute", bottom: "-20px", left: "0" }}
                >
                  {errors.collegename}
                </span>
              )}
            </div>
          </div>

          {isOtherUniversity && (
            <div className="form-group">
              <label className="form-label">Specify University Name *</label>
              <input
                type="text"
                className={`form-input ${errors.collegename ? "input-error" : ""}`}
                placeholder="Enter University Name"
                value={dataToSave["collegename"] || ""}
                disabled={!isOtherUniversity}
                onChange={(e) => {
                  setDataToSave({ ...dataToSave, collegename: e.target.value });
                  if (errors.collegename)
                    setErrors((prev) => ({ ...prev, collegename: "" }));
                }}
              />
              {errors.collegename && (
                <span className="error-text">{errors.collegename}</span>
              )}
            </div>
          )}

          <div className="form-group">
            <label className="form-label">Degree *</label>
            <select
              className={`form-select ${errors.degree ? "input-error" : ""}`}
              name="degree"
              required
              // value={["Bachelor of Engineering (B.E.)", "Bachelor of Technology (B.Tech)", ""].includes(dataToSave["degree"] || "") ? (dataToSave["degree"] || "") : "Equivalent (B.E./B.Tech)"}
              value={
                [
                  "Bachelor of Engineering (B.E.)",
                  "Bachelor of Technology (B.Tech)",
                ].includes(dataToSave["degree"])
                  ? dataToSave["degree"]
                  : dataToSave["degree"]
                    ? "Equivalent (B.E./B.Tech)"
                    : ""
              }
              onChange={(e) => {
                if (e.target.value === "Equivalent (B.E./B.Tech)") {
                  setDataToSave({ ...dataToSave, degree: " " });
                } else {
                  setDataToSave({ ...dataToSave, degree: e.target.value });
                }
                if (errors.degree)
                  setErrors((prev) => ({ ...prev, degree: "" }));
              }}
            >
              <option value="">Select Degree</option>
              {DEGREE_OPTIONS.map((deg, idx) => (
                <option key={idx} value={deg}>
                  {deg}
                </option>
              ))}
            </select>
            {errors.degree && (
              <span className="error-text">{errors.degree}</span>
            )}
          </div>
          {![
            "Bachelor of Engineering (B.E.)",
            "Bachelor of Technology (B.Tech)",
            "",
          ].includes(dataToSave["degree"] || "") && (
            <div className="form-group">
              <label className="form-label">Specify Equivalent Degree *</label>
              <input
                type="text"
                className={`form-input ${errors.degree ? "input-error" : ""}`}
                required
                placeholder="Equivalent Degree"
                value={dataToSave["degree"]?.trim() || ""}
                // placeholder="e"
                onChange={(e) => {
                  setDataToSave({
                    ...dataToSave,
                    degree: e.target.value || " ",
                  });
                  if (errors.degree)
                    setErrors((prev) => ({ ...prev, degree: "" }));
                }}
              />
              {errors.degree && (
                <span className="error-text">{errors.degree}</span>
              )}
            </div>
          )}

          {/* Row 2 */}
          <div className="form-group">
            <label className="form-label">Branch *</label>
            <input
              type="text"
              className={`form-input ${errors.major ? "input-error" : ""}`}
              name="major"
              required
              value={dataToSave["major"] || ""}
              placeholder="Major/Specialization"
              onChange={(e) => {
                setDataToSave({ ...dataToSave, major: e.target.value });
                if (errors.major) setErrors((prev) => ({ ...prev, major: "" }));
              }}
            />
            {errors.major && <span className="error-text">{errors.major}</span>}
          </div>

          <div className="form-group">
            <label className="form-label">Year of Passing *</label>
            <select
              className={`form-select ${errors.passingyear ? "input-error" : ""}`}
              name="passingyear"
              required
              value={dataToSave["passingyear"] || ""}
              onChange={(e) => {
                setDataToSave({ ...dataToSave, passingyear: e.target.value });
                if (errors.passingyear)
                  setErrors((prev) => ({ ...prev, passingyear: "" }));
              }}
            >
              <option value="">Select Year</option>
              {Array.from(
                { length: 30 },
                (_, i) => new Date().getFullYear() - i,
              ).map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
            {errors.passingyear && (
              <span className="error-text">{errors.passingyear}</span>
            )}
          </div>

          {/* Marks Obtained */}
          <div className="form-group">
            <label className="form-label">Marks/CGPA Obtained *</label>
            <input
              type="number"
              className={`form-input ${errors.marks_obtained ? "input-error" : ""}`}
              name="marks_obtained"
              required
              min="0"
              step="any"
              value={dataToSave["marks_obtained"] || ""}
              placeholder="e.g. 650"
              onChange={(e) => {
                setDataToSave({
                  ...dataToSave,
                  marks_obtained: e.target.value,
                });
                if (errors.marks_obtained)
                  setErrors((prev) => ({ ...prev, marks_obtained: "" }));
              }}
            />
            {errors.marks_obtained && (
              <span className="error-text">{errors.marks_obtained}</span>
            )}
          </div>

          {/* Maximum Marks */}
          <div className="form-group">
            <label className="form-label">Maximum Marks/CGPA *</label>
            <input
              type="number"
              className={`form-input ${errors.maximum_marks ? "input-error" : ""}`}
              name="maximum_marks"
              required
              min="1"
              step="any"
              value={dataToSave["maximum_marks"] || ""}
              placeholder="e.g. 1000"
              onChange={(e) => {
                setDataToSave({ ...dataToSave, maximum_marks: e.target.value });
                if (errors.maximum_marks)
                  setErrors((prev) => ({ ...prev, maximum_marks: "" }));
              }}
            />
            {errors.maximum_marks && (
              <span className="error-text">{errors.maximum_marks}</span>
            )}
          </div>

          {/* Calculated Percentage (read-only, from marks) */}
          <div className="form-group">
            <label className="form-label">Percentage</label>
            <input
              type="text"
              className="form-input"
              readOnly
              value={maximumMarks > 0 ? `${percentage.toFixed(2)}%` : "—"}
              style={{
                backgroundColor: "#e9ecef",
                cursor: "not-allowed",
                color:
                  !isPercentageValid && maximumMarks > 0
                    ? "#dc3545"
                    : "#28a745",
                fontWeight: "bold",
              }}
            />
            {!isPercentageValid && maximumMarks > 0 && (
              <small
                style={{
                  color: "#dc3545",
                  fontSize: "12px",
                  marginTop: "4px",
                  display: "block",
                }}
              >
                {marksObtained < 0 ||
                maximumMarks < 0 ||
                (marksObtained / maximumMarks) * 100 > 100
                  ? "Invalid marks."
                  : "Minimum 65% is required to apply for M.Tech programme."}
              </small>
            )}
          </div>
        </div>
      </div>
    );
  },
);

export default CreateGraduationdetails;
